import { lowerFirst } from 'lodash-es';
import { useMemo } from 'react';
import { PartialConfigCategory, SwarmType } from '@/graphql/types';
import { SwarmTypeTranslations } from '../../../enums';
import { isSomeEnum } from '../../../utils/is-some-enum';

type PartialConfigUrlOptions = {
    absolute?: boolean;
};

export const PARTIAL_CONFIG_TYPE_LABELS: Record<PartialConfigCategory, string> = {
    get [PartialConfigCategory.General]() {
        return gettext('PARTIAL_CONFIG_GENERAL_SETTINGS');
    },
    get [PartialConfigCategory.Notification]() {
        return gettext('PARTIAL_CONFIG_NOTIFICATION_CONFIGURATIONS');
    },
    get [PartialConfigCategory.Schedule]() {
        return gettext('PARTIAL_CONFIG_MEASURING_SCHEDULE');
    },
    get [PartialConfigCategory.DailyValueSettings]() {
        return gettext('PARTIAL_CONFIG_DAILY_VALUE_SETTINGS');
    },
    get [PartialConfigCategory.LdenGuideline]() {
        return gettext('PARTIAL_CONFIG_LDEN_GUIDELINE_SETTINGS');
    },
};

export type PartialConfigTypeLabelKey = keyof typeof PARTIAL_CONFIG_TYPE_LABELS;

export const isPartialConfigCategory = isSomeEnum(PartialConfigCategory);
export const isSwarmType = isSomeEnum(SwarmType);

export function isPartialConfigTypeLabelKey(value: string): value is PartialConfigTypeLabelKey {
    return Object.prototype.hasOwnProperty.call(PARTIAL_CONFIG_TYPE_LABELS, value);
}

export function usePartialConfigPageTitle(
    swarmType: SwarmType,
    partialConfigType: PartialConfigCategory,
    isEdit: boolean
): string {
    return useMemo(() => {
        // Lowercase the label for a smoother title, but keep the LDEN acronym intact.
        const partialConfigTypeLabel = lowerFirst(
            PARTIAL_CONFIG_TYPE_LABELS[partialConfigType]
        ).replace(/lden/gi, 'LDEN');
        const baseText = isEdit
            ? gettext('PARTIAL_CONFIG_PAGE_TITLE_EDIT')
            : gettext('PARTIAL_CONFIG_PAGE_TITLE_CREATE');

        return interpolate(
            baseText,
            {
                swarmType: SwarmTypeTranslations[swarmType],
                partialConfigType: partialConfigTypeLabel,
            },
            true
        );
    }, [swarmType, partialConfigType, isEdit]);
}

export function createPartialConfigCreateUrl(
    swarmType: SwarmType,
    partialConfigType: PartialConfigCategory,
    { absolute = true }: PartialConfigUrlOptions = {}
) {
    const prefix = absolute ? '/partial_config/create' : 'create';
    return `${prefix}/${swarmType}/${partialConfigType}`;
}

export function createPartialConfigEditUrl(
    id: string,
    swarmType: SwarmType,
    partialConfigType: PartialConfigCategory,
    { absolute = true }: PartialConfigUrlOptions = {}
) {
    const prefix = absolute ? '/partial_config/edit' : 'edit';
    return `${prefix}/${swarmType}/${partialConfigType}/${id}`;
}
